import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, ChevronRight, Search, Swords, UserPlus, Wifi, X } from 'lucide-react';

import { FriendListItem } from '@/components/social/FriendListItem';
import { UserAvatar } from '@/components/social/UserAvatar';
import { Loader } from '@/components/common/Loader';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useHaptics } from '@/hooks/useHaptics';
import {
  useGetOnlineFriendsFeedQuery,
  useListFollowersQuery,
  useListFollowingQuery,
  useListFriendRequestsQuery,
  useListFriendsQuery,
  useRequestFriendshipMutation,
  useSearchUsersQuery,
} from '@/services/socialApi';
import {
  useAcceptChallengeMutation,
  useCancelChallengeMutation,
  useDeclineChallengeMutation,
  useListChallengesQuery,
} from '@/services/qduelsApi';
import { duelFormatLabelKey, duelGameModeLabelKey } from '@/types/qduels';
import type { ChallengeDTO } from '@/types/qduels';
import type { UserSearchResult } from '@/types/social';
import { cn } from '@/lib/cn';
import { Segmented } from '@/components/common/Segmented';

/** Minimum query length before we hit the server search endpoint. */
const MIN_SEARCH_LEN = 2;

type SubTab = 'friends' | 'following' | 'followers' | 'requests' | 'duels';

/**
 * Friends tab — entry point for the QSocials wave-1 surface.
 *
 * Four sub-tabs (friends / following / followers / requests) implemented as
 * pill switcher inside the page rather than dedicated routes. Keeps the URL
 * scheme simple (`/friends`) and matches the "tab inside route" pattern used
 * by Tournaments + TournamentDetail.
 *
 * Top section:
 *   - Search input. With < 2 chars it's a client-side filter over the loaded
 *     list (the active sub-tab). With ≥ 2 chars it switches to a debounced
 *     server-side discovery search (`GET /social/users/search`, Wave 2) that
 *     surfaces *any* QLASH user with an "Add friend" action — not just people
 *     already in your lists.
 *   - "Online now" pill that flips into the requests query → shows the
 *     online-friends feed
 */
const SUB_TABS: readonly SubTab[] = ['friends', 'following', 'followers', 'requests', 'duels'];

function isSubTab(value: string | null): value is SubTab {
  return value !== null && (SUB_TABS as readonly string[]).includes(value);
}

export function Friends() {
  const { t } = useTranslation('general');
  const haptics = useHaptics();
  const [searchParams] = useSearchParams();

  // Deep-link support: `/friends?tab=duels` lands directly on the Sfide
  // sub-tab (used by the Hub "Sfida" button + the QueueSheet "Sfida un amico"
  // CTA). Read once on mount — the pill switcher owns the tab after that.
  const [tab, setTab] = useState<SubTab>(() => {
    const param = searchParams.get('tab');
    return isSubTab(param) ? param : 'friends';
  });
  const [query, setQuery] = useState('');
  const [onlineOnly, setOnlineOnly] = useState(false);

  // Server-side discovery search kicks in at >= 2 chars (debounced).
  const debouncedQuery = useDebouncedValue(query.trim(), 300);
  const searchActive = debouncedQuery.length >= MIN_SEARCH_LEN;
  const search = useSearchUsersQuery({ q: debouncedQuery, limit: 20 }, { skip: !searchActive });

  const friends = useListFriendsQuery(undefined, { refetchOnFocus: true });
  const requests = useListFriendRequestsQuery(undefined, {
    refetchOnFocus: true,
  });
  const following = useListFollowingQuery(undefined, { refetchOnFocus: true });
  const followers = useListFollowersQuery(undefined, { refetchOnFocus: true });
  const onlineFriends = useGetOnlineFriendsFeedQuery(undefined, {
    refetchOnFocus: true,
    pollingInterval: 30_000,
    skip: !onlineOnly,
  });

  // Incoming duel challenges (role=defender). Polled fast while the Sfide tab
  // is open (live 5-min window), slow otherwise just to keep the badge fresh.
  const onDuels = tab === 'duels';
  const incomingChallenges = useListChallengesQuery(
    { role: 'defender' },
    { pollingInterval: onDuels ? 5_000 : 20_000, refetchOnFocus: true },
  );
  const incomingPendingCount = useMemo(
    () => (incomingChallenges.data ?? []).filter((c) => c.status === 'pending').length,
    [incomingChallenges.data],
  );

  const counts = {
    friends: friends.data?.length ?? 0,
    following: following.data?.length ?? 0,
    followers: followers.data?.length ?? 0,
    requests: (requests.data?.incoming.length ?? 0) + (requests.data?.outgoing.length ?? 0),
    duels: incomingPendingCount,
  };

  const switchTab = (next: SubTab) => {
    if (next === tab) return;
    haptics.selection();
    setTab(next);
  };

  const onlineSet = useMemo(() => {
    if (!onlineOnly) return null;
    const ids = onlineFriends.data?.feed.map((f) => f.userId) ?? [];
    return new Set(ids);
  }, [onlineOnly, onlineFriends.data]);

  // Client-side filter for the loaded lists, used only for short queries
  // (< 2 chars) and the "online only" toggle. At >= 2 chars the UI switches
  // to the server-side discovery search instead (searchActive). Matches on
  // displayName (now available via enriched lists) as well as raw id.
  const filterByQuery = <
    T extends {
      friendUserId?: string;
      userId?: string;
      displayName?: string | null;
    },
  >(
    items: T[],
  ): T[] => {
    const q = query.trim().toLowerCase();
    if (!q && !onlineSet) return items;
    return items.filter((item) => {
      const id = (item.friendUserId ?? item.userId) || '';
      const name = (item.displayName ?? '').toLowerCase();
      const passQ = !q || id.toLowerCase().includes(q) || name.includes(q);
      const passOnline = !onlineSet || onlineSet.has(id);
      return passQ && passOnline;
    });
  };

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
      {/* Le tre statistiche sociali, arrivate qui dal Profilo — che così resta
          alla sola identità e lascia tutto lo spazio all'avatar.
          In forma di BARRA e non di riquadri: sono tre numeri di due cifre, e
          impilati (numero sopra, etichetta sotto) occupavano l'altezza di una
          card per dire pochissimo. Numero PRIMA dell'etichetta, sulla stessa
          riga: è il valore che si cerca, l'etichetta dice solo di cosa.
          Non è un comando: le stesse tre voci sono schede navigabili qui sotto,
          e renderla toccabile darebbe due modi di fare lo stesso salto. */}
      <div className="card-edge flex items-center gap-1 rounded-2xl bg-bg-secondary px-2 py-2">
        <SocialStat
          label={t('social.tab_friends', { defaultValue: 'Friends' })}
          count={counts.friends}
        />
        <span aria-hidden className="divider-edge h-5 border-l" />
        <SocialStat
          label={t('social.tab_following', { defaultValue: 'Following' })}
          count={counts.following}
        />
        <span aria-hidden className="divider-edge h-5 border-l" />
        <SocialStat
          label={t('social.tab_followers', { defaultValue: 'Followers' })}
          count={counts.followers}
        />
      </div>

      <header>
        <h1 className="text-2xl font-bold text-fg">
          {t('social.friends_title', { defaultValue: 'Friends' })}
        </h1>
        <p className="mt-1 text-sm text-fg-muted">
          {t('social.friends_subtitle', {
            defaultValue: 'Follow, watch and cheer your QLASH crew.',
          })}
        </p>
      </header>

      {/* Search + online filter */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search
            aria-hidden
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('social.search_placeholder', {
              defaultValue: 'Search players by name',
            })}
            className={cn(
              'card-edge h-10 w-full rounded-xl bg-bg-secondary pl-9 pr-3 text-sm text-fg outline-none',
              'placeholder:text-fg-muted focus:ring-1 focus:ring-brand',
            )}
          />
        </div>
        <button
          type="button"
          onClick={() => {
            haptics.selection();
            setOnlineOnly((v) => !v);
          }}
          className={cn(
            'flex h-10 items-center gap-1.5 rounded-xl px-3 text-xs font-bold uppercase tracking-wide transition-colors',
            onlineOnly ? 'bg-brand text-brand-fg' : 'card-edge bg-bg-secondary text-fg-muted',
          )}
          aria-pressed={onlineOnly}
        >
          <Wifi className="h-4 w-4" />
          {t('social.online_now', { defaultValue: 'Online' })}
        </button>
      </div>

      {/* Discovery search results (>= 2 chars) — server-side, any QLASH user */}
      {searchActive && (
        <SearchResults
          isFetching={search.isFetching}
          isError={search.isError}
          results={search.data ?? []}
        />
      )}

      {/* Sub-tab pills + list content (hidden while discovery search is active) */}
      {!searchActive && (
        <>
          {/* Cinque voci fisse che ci stanno tutte: segmentato, non chip a
          scorrimento. Erano in un `overflow-x-auto` che non scorreva mai —
          quindi pagavano il costo delle chip separate (selezionata e
          alternative sullo stesso piano) senza averne il motivo. */}
          <Segmented
            value={tab}
            onChange={switchTab}
            options={(['friends', 'following', 'followers', 'requests', 'duels'] as const).map(
              (key) => ({
                value: key,
                count: counts[key],
                label: t(`social.tab_${key}`, {
                  defaultValue:
                    key === 'friends'
                      ? 'Friends'
                      : key === 'following'
                        ? 'Following'
                        : key === 'followers'
                          ? 'Followers'
                          : key === 'requests'
                            ? 'Requests'
                            : 'Duels',
                }),
              }),
            )}
          />

          {/* Content */}
          {tab === 'friends' && (
            <ListContainer
              isLoading={friends.isLoading}
              isError={friends.isError}
              empty={
                !friends.data?.length
                  ? t('social.empty_friends', {
                      defaultValue: 'No friends yet. Send a request from a profile.',
                    })
                  : null
              }
            >
              <ul className="flex flex-col gap-2">
                {filterByQuery(friends.data ?? []).map((f) => (
                  <FriendListItem
                    key={f.friendUserId}
                    userId={f.friendUserId}
                    displayName={f.displayName}
                    avatarUrl={f.avatarUrl}
                    mode="friend"
                  />
                ))}
              </ul>
            </ListContainer>
          )}

          {tab === 'following' && (
            <ListContainer
              isLoading={following.isLoading}
              isError={following.isError}
              empty={
                !following.data?.length
                  ? t('social.empty_following', {
                      defaultValue: "You're not following anyone yet.",
                    })
                  : null
              }
            >
              <ul className="flex flex-col gap-2">
                {filterByQuery(following.data ?? []).map((f) => (
                  <FriendListItem
                    key={f.userId}
                    userId={f.userId}
                    displayName={f.displayName}
                    avatarUrl={f.avatarUrl}
                    mode="follow"
                  />
                ))}
              </ul>
            </ListContainer>
          )}

          {tab === 'followers' && (
            <ListContainer
              isLoading={followers.isLoading}
              isError={followers.isError}
              empty={
                !followers.data?.length
                  ? t('social.empty_followers', {
                      defaultValue: 'No followers yet.',
                    })
                  : null
              }
            >
              <ul className="flex flex-col gap-2">
                {filterByQuery(followers.data ?? []).map((f) => (
                  <FriendListItem
                    key={f.userId}
                    userId={f.userId}
                    displayName={f.displayName}
                    avatarUrl={f.avatarUrl}
                    mode="follower"
                  />
                ))}
              </ul>
            </ListContainer>
          )}

          {tab === 'requests' && (
            <ListContainer
              isLoading={requests.isLoading}
              isError={requests.isError}
              empty={
                !counts.requests
                  ? t('social.empty_requests', {
                      defaultValue: 'No pending requests.',
                    })
                  : null
              }
            >
              <div className="flex flex-col gap-4">
                {requests.data?.incoming.length ? (
                  <section>
                    <h2 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-fg-muted">
                      {t('social.incoming', { defaultValue: 'Incoming' })}
                    </h2>
                    <ul className="flex flex-col gap-2">
                      {filterByQuery(requests.data.incoming).map((r) => (
                        <FriendListItem
                          key={r.friendUserId}
                          userId={r.friendUserId}
                          displayName={r.displayName}
                          avatarUrl={r.avatarUrl}
                          mode="incoming"
                          hidePresence
                        />
                      ))}
                    </ul>
                  </section>
                ) : null}
                {requests.data?.outgoing.length ? (
                  <section>
                    <h2 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-fg-muted">
                      {t('social.outgoing', { defaultValue: 'Sent' })}
                    </h2>
                    <ul className="flex flex-col gap-2">
                      {filterByQuery(requests.data.outgoing).map((r) => (
                        <FriendListItem
                          key={r.friendUserId}
                          userId={r.friendUserId}
                          displayName={r.displayName}
                          avatarUrl={r.avatarUrl}
                          mode="outgoing"
                          hidePresence
                        />
                      ))}
                    </ul>
                  </section>
                ) : null}
              </div>
            </ListContainer>
          )}

          {tab === 'duels' && (
            <ChallengesPanel
              incoming={incomingChallenges.data ?? []}
              incomingLoading={incomingChallenges.isLoading}
              incomingError={incomingChallenges.isError}
            />
          )}
        </>
      )}
    </div>
  );
}

/**
 * Discovery search results panel (Wave 2). Renders server-side
 * `GET /social/users/search` hits, each with an "Add friend" action that
 * fires `POST /social/friends/request`.
 */
function SearchResults({
  isFetching,
  isError,
  results,
}: {
  isFetching: boolean;
  isError: boolean;
  results: UserSearchResult[];
}) {
  const { t } = useTranslation('general');

  if (isFetching && results.length === 0) {
    return (
      <div className="flex h-24 items-center justify-center">
        <Loader />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="card-edge rounded-xl bg-bg-secondary p-6 text-center text-sm text-danger">
        {t('social.search_failed', { defaultValue: 'Search failed.' })}
      </div>
    );
  }
  if (results.length === 0) {
    return (
      <div className="card-edge rounded-xl bg-bg-secondary p-6 text-center text-sm text-fg-muted">
        {t('social.search_empty', { defaultValue: 'No players found.' })}
      </div>
    );
  }
  return (
    <ul className="flex flex-col gap-2">
      {results.map((r) => (
        <SearchResultRow key={r.userId} result={r} />
      ))}
    </ul>
  );
}

function SearchResultRow({ result }: { result: UserSearchResult }) {
  const { t } = useTranslation('general');
  const haptics = useHaptics();
  const navigate = useNavigate();
  const [requestFriendship, { isLoading, isSuccess }] = useRequestFriendshipMutation();

  const name = result.displayName || t('social.unknown_user', { defaultValue: 'QLASH player' });

  const onAdd = () => {
    if (isLoading || isSuccess) return;
    haptics.impact('light');
    requestFriendship({ friendUserId: result.userId });
  };

  const openProfile = () => {
    haptics.selection();
    navigate(`/profile/${encodeURIComponent(result.userId)}`);
  };

  return (
    <li className="flex items-center gap-3 rounded-xl bg-bg-secondary p-3">
      <button
        type="button"
        onClick={openProfile}
        className="flex min-w-0 flex-1 items-center gap-3 text-left transition-opacity active:opacity-70"
      >
        <UserAvatar userId={result.userId} label={name} avatarUrl={result.avatarUrl} size="md" />
        <p className="min-w-0 flex-1 truncate text-sm font-semibold text-fg">{name}</p>
      </button>
      <button
        type="button"
        onClick={onAdd}
        disabled={isLoading || isSuccess}
        className={cn(
          'flex h-8 items-center gap-1.5 rounded-full px-3 text-xs font-bold uppercase tracking-wide transition-opacity active:opacity-80 disabled:opacity-60',
          isSuccess ? 'bg-bg-tertiary text-fg-muted' : 'bg-brand text-brand-fg',
        )}
        aria-label={t('social.add_friend', { defaultValue: 'Add friend' })}
      >
        {isSuccess ? (
          <>
            <Check className="h-4 w-4" />
            {t('social.request_sent', { defaultValue: 'Sent' })}
          </>
        ) : (
          <>
            <UserPlus className="h-4 w-4" />
            {t('social.add_friend', { defaultValue: 'Add' })}
          </>
        )}
      </button>
    </li>
  );
}

function ListContainer({
  isLoading,
  isError,
  empty,
  children,
}: {
  isLoading: boolean;
  isError: boolean;
  empty: string | null;
  children: React.ReactNode;
}) {
  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="card-edge rounded-xl bg-bg-secondary p-6 text-center text-sm text-danger">
        Failed to load.
      </div>
    );
  }
  if (empty) {
    return (
      <div className="card-edge rounded-xl bg-bg-secondary p-6 text-center text-sm text-fg-muted">
        {empty}
      </div>
    );
  }
  return <>{children}</>;
}

/** Challenge lifecycle states that still need attention / are live. */
const ACTIVE_STATUSES = new Set<ChallengeDTO['status']>(['pending', 'accepted', 'in_progress']);

/**
 * The CR game tag to open in DuelResult for a finished challenge: the decisive
 * (last) match in `matchTags`, falling back to `currentMatchTag`. Returns null
 * when the challenge carries no match tag (declined/expired/cancelled before any
 * match was played) — the row then stays inert.
 */
function challengeResultTag(c: ChallengeDTO): string | null {
  const last = c.matchTags?.[c.matchTags.length - 1];
  return last ?? c.currentMatchTag ?? null;
}

/**
 * "Sfide" tab — in-app duel challenges. Incoming (role=defender) can be
 * accepted/declined; outgoing (role=challenger) can be cancelled while pending.
 * On accept, qduels-api creates the hosted Supercell match and BOTH players get
 * the in-game invite — so the flow lives entirely in-app, no QBot DM required.
 * The incoming list is polled by the parent; outgoing is polled here.
 */
function ChallengesPanel({
  incoming,
  incomingLoading,
  incomingError,
}: {
  incoming: ChallengeDTO[];
  incomingLoading: boolean;
  incomingError: boolean;
}) {
  const { t } = useTranslation('general');
  const haptics = useHaptics();
  const navigate = useNavigate();

  const outgoingQuery = useListChallengesQuery(
    { role: 'challenger' },
    { pollingInterval: 6_000, refetchOnFocus: true },
  );
  const outgoing = outgoingQuery.data ?? [];

  const [accept, acceptState] = useAcceptChallengeMutation();
  const [decline, declineState] = useDeclineChallengeMutation();
  const [cancel, cancelState] = useCancelChallengeMutation();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const busy = acceptState.isLoading || declineState.isLoading || cancelState.isLoading;

  const run = async (
    id: string,
    fn: (id: string) => { unwrap: () => Promise<unknown> },
    impact: 'success' | 'error' = 'success',
  ) => {
    if (busy) return;
    setBusyId(id);
    setActionError(null);
    haptics.impact('medium');
    try {
      await fn(id).unwrap();
      haptics.notification(impact);
    } catch {
      haptics.notification('error');
      setActionError(
        t('social.duel_action_error', {
          defaultValue: 'Action failed — the challenge may have expired. Try again.',
        }),
      );
    } finally {
      setBusyId(null);
    }
  };

  const openResult = (c: ChallengeDTO) => {
    const tag = challengeResultTag(c);
    if (!tag) return;
    haptics.impact('light');
    navigate(
      `/duels/result/${encodeURIComponent(tag)}?challengeId=${encodeURIComponent(c.challengeId)}`,
    );
  };

  const activeIncoming = incoming.filter((c) => ACTIVE_STATUSES.has(c.status));
  const activeOutgoing = outgoing.filter((c) => ACTIVE_STATUSES.has(c.status));
  const recent = [...incoming, ...outgoing]
    .filter((c) => !ACTIVE_STATUSES.has(c.status))
    .sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''))
    .slice(0, 5);

  if (incomingLoading && !incoming.length) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader />
      </div>
    );
  }
  if (incomingError) {
    return (
      <div className="card-edge rounded-xl bg-bg-secondary p-6 text-center text-sm text-danger">
        Failed to load.
      </div>
    );
  }

  const nothing = !activeIncoming.length && !activeOutgoing.length && !recent.length;
  if (nothing) {
    return (
      <div className="card-edge rounded-xl bg-bg-secondary p-6 text-center text-sm text-fg-muted">
        {t('social.empty_duels', {
          defaultValue: 'No challenges. Tap "Sfida" on a friend to start a duel.',
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {actionError && (
        <div className="rounded-xl bg-danger/15 p-3 text-center text-xs text-danger">
          {actionError}
        </div>
      )}

      {activeIncoming.length > 0 && (
        <section>
          <h2 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-fg-muted">
            {t('social.incoming', { defaultValue: 'Incoming' })}
          </h2>
          <ul className="flex flex-col gap-2">
            {activeIncoming.map((c) => (
              <ChallengeCard
                key={c.challengeId}
                challenge={c}
                role="incoming"
                busy={busyId === c.challengeId}
                onAccept={() => run(c.challengeId, accept)}
                onDecline={() => run(c.challengeId, decline)}
              />
            ))}
          </ul>
        </section>
      )}

      {activeOutgoing.length > 0 && (
        <section>
          <h2 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-fg-muted">
            {t('social.outgoing', { defaultValue: 'Sent' })}
          </h2>
          <ul className="flex flex-col gap-2">
            {activeOutgoing.map((c) => (
              <ChallengeCard
                key={c.challengeId}
                challenge={c}
                role="outgoing"
                busy={busyId === c.challengeId}
                onCancel={() => run(c.challengeId, cancel)}
              />
            ))}
          </ul>
        </section>
      )}

      {recent.length > 0 && (
        <section>
          <h2 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-fg-muted">
            {t('social.duel_recent', { defaultValue: 'Recent' })}
          </h2>
          <ul className="flex flex-col gap-2">
            {recent.map((c) => (
              <ChallengeCard
                key={c.challengeId}
                challenge={c}
                role="recent"
                busy={false}
                onOpenResult={
                  c.status === 'completed' && challengeResultTag(c)
                    ? () => openResult(c)
                    : undefined
                }
              />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/** A single duel challenge row, action set driven by `role` + status. */
function ChallengeCard({
  challenge: c,
  role,
  busy,
  onAccept,
  onDecline,
  onCancel,
  onOpenResult,
}: {
  challenge: ChallengeDTO;
  role: 'incoming' | 'outgoing' | 'recent';
  busy: boolean;
  onAccept?: () => void;
  onDecline?: () => void;
  onCancel?: () => void;
  /** When set (completed challenge), the whole row opens DuelResult on tap. */
  onOpenResult?: () => void;
}) {
  const { t } = useTranslation('general');

  // The opponent is whichever side I'm NOT. For 'recent' rows we can't be sure
  // which side the viewer was without their sub, so we show the challenger.
  const meIsDefender = role === 'incoming';
  const opp = meIsDefender ? c.challenger : c.defender;
  const oppName =
    opp.displayName?.trim() ||
    opp.playerTag ||
    t('social.duel_unknown_player', { defaultValue: 'Player' });
  const myWins = meIsDefender ? (c.winsDefender ?? 0) : (c.winsChallenger ?? 0);
  const oppWins = meIsDefender ? (c.winsChallenger ?? 0) : (c.winsDefender ?? 0);

  const live = c.status === 'in_progress' || c.status === 'accepted';
  const tappable = Boolean(onOpenResult);

  return (
    <li
      className={cn(
        'rounded-xl bg-bg-secondary p-3',
        tappable && 'cursor-pointer transition-opacity active:opacity-80',
      )}
      {...(tappable ? { role: 'button', tabIndex: 0, onClick: onOpenResult } : {})}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-bg-tertiary text-fg-muted">
          <Swords className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-fg">{oppName}</p>
          <p className="truncate text-xs text-fg-muted">
            {t(duelFormatLabelKey(c.format), { defaultValue: c.format.toUpperCase() })}
            {c.gameMode
              ? ` · ${t(duelGameModeLabelKey(c.gameMode), { defaultValue: c.gameMode })}`
              : ''}
            {live ? ` · ${myWins}–${oppWins}` : ''}
          </p>
        </div>

        {role === 'incoming' && c.status === 'pending' && (
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={onDecline}
              aria-label={t('social.duel_decline', { defaultValue: 'Decline' })}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-bg-tertiary text-fg-muted disabled:opacity-50"
            >
              <X className="h-4 w-4" />
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={onAccept}
              aria-label={t('social.duel_accept', { defaultValue: 'Accept' })}
              className="flex h-8 items-center gap-1 rounded-full bg-brand px-3 text-xs font-bold text-brand-fg disabled:opacity-50"
            >
              <Check className="h-4 w-4" />
              {t('social.duel_accept', { defaultValue: 'Accept' })}
            </button>
          </div>
        )}

        {role === 'outgoing' && c.status === 'pending' && (
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="shrink-0 rounded-full bg-bg-tertiary px-3 py-1.5 text-xs font-bold text-fg-muted disabled:opacity-50"
          >
            {t('social.duel_cancel', { defaultValue: 'Cancel' })}
          </button>
        )}

        {tappable && <ChevronRight aria-hidden className="h-4 w-4 shrink-0 text-fg-muted" />}
      </div>

      {live && (
        <p className="mt-2 rounded-lg bg-brand/10 px-3 py-2 text-xs font-medium text-brand">
          {t('social.duel_open_cr', {
            defaultValue: 'Match on — open Clash Royale and accept the invite to play!',
          })}
        </p>
      )}

      {role === 'recent' && (
        <p className="mt-1 text-xs text-fg-muted">{statusLabel(c.status, t)}</p>
      )}
    </li>
  );
}

/**
 * Una statistica sociale: numero ed etichetta sulla STESSA riga.
 *
 * L'etichetta si tronca invece di andare a capo: una lingua più lunga farebbe
 * crescere l'altezza della barra, e un troncamento è meno grave di tre
 * statistiche disallineate.
 */
function SocialStat({ label, count }: { label: string; count: number }) {
  return (
    <span className="flex min-w-0 flex-1 items-baseline justify-center gap-1">
      <span className="text-sm font-bold text-fg">{count}</span>
      <span className="truncate text-[10px] uppercase tracking-wide text-fg-muted">{label}</span>
    </span>
  );
}

function statusLabel(
  status: ChallengeDTO['status'],
  t: (k: string, o: { defaultValue: string }) => string,
): string {
  switch (status) {
    case 'completed':
      return t('social.duel_completed', { defaultValue: 'Completed' });
    case 'declined':
      return t('social.duel_declined', { defaultValue: 'Declined' });
    case 'cancelled':
      return t('social.duel_cancelled', { defaultValue: 'Cancelled' });
    case 'expired':
      return t('social.duel_expired', { defaultValue: 'Expired (not accepted in time)' });
    case 'abandoned':
      return t('social.duel_abandoned', { defaultValue: 'Abandoned' });
    default:
      return status;
  }
}
